import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaEuroSign, FaWallet, FaChartPie, FaListCheck } from 'react-icons/fa6';
import BankAccountsPage from './BankAccountsPage';
import MemberBalanceCards from '../../components/charts/MemberBalanceCards';
import IncomeExpensesChart from '../../components/charts/IncomeExpensesChart';
import FinancialKpiCard from '../../components/financial/FinancialKpiCard';

import PageHeader from '../../components/misc/PageHeader';
import useSelectedCommunity from '../../../hooks/community/useSelectedCommunity';
import * as bankAccountService from '../../../services/community/bankAccountService';
import connectionService from '../../../services/community/connectionService';
import { BankAccount, Currency } from '../../../types/bankAccount';
import { formatEuroCents } from '../../../utils/bankAccountUtil';
import { Connection, ConnectionType } from '../../../redux/slices/connectionsSlice';

interface FinancialOverviewPageProps {
  hideHeader?: boolean;
}

interface FinancialMetrics {
  totalBalance: number;
  totalWalletBalance: number;
  totalEquity: number;
}

export default function FinancialOverviewPage({
  hideHeader = false,
}: FinancialOverviewPageProps): JSX.Element {
  const { t, i18n } = useTranslation('', { keyPrefix: 'page.financial.overview' });
  const community = useSelectedCommunity();

  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [metrics, setMetrics] = useState<FinancialMetrics>({
    totalBalance: 0,
    totalWalletBalance: 0,
    totalEquity: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (community) {
      loadFinancialData();
    }
  }, [community]);

  const loadFinancialData = async () => {
    try {
      setLoading(true);
      const accounts = await bankAccountService.getAllBankAccounts();
      setBankAccounts(accounts);

      const totalBalance = accounts.reduce((sum, acc) => sum + acc.balance, 0);

      // Total wallet balance: sum of all members' wallet balances
      let totalWalletBalance = 0;
      try {
        const connections = await connectionService.getConnections();
        const members = connections.filter(
          (c: Connection) =>
            c.type === ConnectionType.MEMBER || c.type === ConnectionType.EXTERNAL_MEMBER,
        );
        const walletPromises = members.map(async (c: Connection) => {
          try {
            const balance = await bankAccountService.getMemberWalletBalance(
              +c.dashboardMemberId,
              Currency.EUR,
            );
            return balance.balance;
          } catch {
            return 0;
          }
        });
        const balances = await Promise.all(walletPromises);
        totalWalletBalance = balances.reduce((sum, b) => sum + b, 0);
      } catch (err) {
        console.error('Error loading wallet balances:', err);
      }

      // Total equity from balance sheet
      let totalEquity = 0;
      try {
        const balanceSheet = await bankAccountService.getBalanceStatement();
        totalEquity = balanceSheet?.liabilitiesAndEquity?.equity?.totalEquity ?? 0;
      } catch (err) {
        console.error('Error loading balance sheet:', err);
      }

      setMetrics({
        totalBalance,
        totalWalletBalance,
        totalEquity,
      });
    } catch (error) {
      console.error('Error loading financial data:', error);
    } finally {
      setLoading(false);
    }
  };
  const isLoading = loading;

  return (
    <>
      {!hideHeader && <PageHeader title={t('title')} subtitle={t('subtitle')} />}

      <div className="flex flex-col gap-8">
        {/* Top tiles: Total saldi, Wallets, Total equity, Todo's */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <FinancialKpiCard
            label={t('cards.totalBalance')}
            value={!isLoading ? formatEuroCents(metrics.totalBalance, i18n.language) : undefined}
            loading={isLoading}
            icon={<FaEuroSign />}
            iconBgClassName="rounded-[8px] bg-primary-100 p-3"
            iconClassName="h-6 w-6 text-primary-900"
          />

          <FinancialKpiCard
            label={t('cards.wallets')}
            value={!isLoading ? formatEuroCents(metrics.totalWalletBalance, i18n.language) : undefined}
            loading={isLoading}
            icon={<FaWallet />}
            iconBgClassName="rounded-[8px] bg-primary-100 p-3"
            iconClassName="h-6 w-6 text-primary-900"
          />

          <FinancialKpiCard
            label={t('cards.totalEquity')}
            value={!isLoading ? formatEuroCents(metrics.totalEquity, i18n.language) : undefined}
            loading={isLoading}
            icon={<FaChartPie />}
            iconBgClassName="rounded-[8px] bg-primary-100 p-3"
            iconClassName="h-6 w-6 text-primary-900"
          />

          <FinancialKpiCard
            label={t('cards.todos')}
            subtitle={t('cards.comingSoon')}
            icon={<FaListCheck />}
            iconBgClassName="rounded-[8px] bg-primary-100 p-3"
            iconClassName="h-6 w-6 text-primary-900"
          />
        </div>

        {isLoading ? (
          <div className="flex flex-col gap-6">
            <div className="h-40 rounded-[12px] bg-gray-100 animate-pulse" />
            <div className="h-40 rounded-[12px] bg-gray-100 animate-pulse" />
            <div className="h-64 rounded-[12px] bg-gray-100 animate-pulse" />
          </div>
        ) : (
          <>
            {/* Bank accounts (same as Settings > Bank Accounts tab) */}
            <BankAccountsPage hideHeader />

            {/* Member Balance Cards */}
            <MemberBalanceCards />

            {/* Income and Expenses Chart */}
            <IncomeExpensesChart bankAccounts={bankAccounts} />
          </>
        )}
      </div>
    </>
  );
}
